import { Box, Stack } from "@mui/material";
import type {
  GridCallbackDetails,
  GridFilterModel,
  GridRowEditStartParams,
  GridRowSelectionModel,
  GridSortModel,
  GridColumnVisibilityModel,
  GridRowParams,
  GridColDef,
  GridPinnedColumns,
} from "@mui/x-data-grid-pro";
import {
  DataGridPro,
  GridLoadingOverlay,
  GridLogicOperator,
  GridRowEditStopParams,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  jaJP,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns-tz";
import { isColBoolean } from "./Col/ColBoolean";
import { isColCustom } from "./Col/ColCustom";
import { isColDate } from "./Col/ColDate";
import { isColEnum } from "./Col/ColEnum";
import { isColHidden } from "./Col/ColHidden";
import { isColIntTime } from "./Col/ColIntTime";
import { isColNumber } from "./Col/ColNumber";
import { isColString } from "./Col/ColString";
import { isColTime } from "./Col/ColTime";
import { ColBooleanDef } from "./ColDefObject/ColBooleanDef";
import { ColCustomDef } from "./ColDefObject/ColCustomDef";
import { ColDateDef } from "./ColDefObject/ColDateDef";
import { ColEnumDef } from "./ColDefObject/ColEnumDef";
import { ColHiddenDef } from "./ColDefObject/ColHiddenDef";
import { ColIntTimeDef } from "./ColDefObject/ColIntTimeDef";
import { ColNumberDef } from "./ColDefObject/ColNumberDef";
import { ColStringDef } from "./ColDefObject/ColStringDef";
import { ColTimeDef } from "./ColDefObject/ColTimeDef";
import { ErrorLayer } from "./error/ErrorLayer";
import { convertToNestedFilterQuery } from "./lib/filterModel";
import { mergeQueryVariables } from "./lib/mergeQueryVariables";
import { useRowBooleanState, useRowStringState } from "./lib/useRowState";
import { randomString } from "./random";
import { ColWithField } from "./Col/Col";
import { ColCheckBox } from "./Col/ColCheckBox";
// v2用で追加する。このファイルからはDatagridAsyncV2のみをexportするために、DatagridAsyncから必要なものをimportしている
import {
  DatagridHeaderProps,
  DataGridRowDefinition,
  MutationProps,
  OrderDirectionEnum,
} from "./DatagridAsync";

const rowsPerPageDefault = 20;

type RefetchProps = {
  first?: number | null;
  after?: string | null;
  query?: any;
  order?: any[] | any | null;
};
type RefetchFn = (props: RefetchProps) => void;
// export type DataGridRowDefinition = Array<Col>;

type DataLike = {
  readonly id: string | undefined | null;
} & Record<string, unknown>;
interface Props<T extends DataLike> {
  loading: boolean;
  createRow?: ((input: MutationProps) => Promise<boolean>) | undefined;
  updateRow?: (id: string | number, input: MutationProps) => Promise<boolean>;
  deleteRow?: (id: string | number) => Promise<boolean>;
  onRowSelectionModelChange?: (
    rowSelectionModel: GridRowSelectionModel,
    details: GridCallbackDetails,
  ) => void;
  data: readonly T[];
  total: number;
  variables?: RefetchProps;
  filterModel?: any;
  dataGridFilterModel?: GridFilterModel;
  onDataGridFilterModelChange?: (model: GridFilterModel) => void;
  error?: string;
  refetch: RefetchFn;
  header?: (props: DatagridHeaderProps) => JSX.Element;
  colDef: DataGridRowDefinition<T>;
  rowsPerPageOptions?: number[];
  dataGridPage?: number;
  onDataGridPageChange?: (page: number) => void;
  dataGridSortModel?: GridSortModel;
  dataGridColumnVisibilityModel?: GridColumnVisibilityModel;
  onDataGridColumnVisibilityModelChange?: (
    model: GridColumnVisibilityModel,
  ) => void;
  dataGridPinnedColumnFields?: GridPinnedColumns;
  onDataGridPinnedColumnFieldsChange?: (model: GridPinnedColumns) => void;
  isRowSelectable?: (props: GridRowParams<T>) => boolean;
  filterMode?: "client" | "server";
  paginationMode?: "client" | "server";
  defaultLimitRows?: number;
}

type FieldNameColTypeMapType = {
  [key: string]:
    | "string"
    | "custom"
    | "checkBox"
    | "date"
    | "enum"
    | "hidden"
    | "intTime"
    | "number"
    | "time"
    | "boolean";
};

const styles = {
  datagrid: {
    width: "100%",
    height: "640px",
    "& .MuiDataGrid-columnHeader": {
      fontSize: "13px",
      padding: "0 4px",
    },
    "& .MuiDataGrid-columnHeader--alignCenter": {
      "& .MuiDataGrid-columnHeaderTitleContainer": {
        justifyContent: "left",
      },
    },
    "& .MuiDataGrid-cell": {
      fontSize: "13px",
      padding: "0 4px",
      borderRight: "1px solid #e5e5e5",
    },
    "& .MuiDataGrid-cell--textCenter": {
      justifyContent: "left",
    },
    "& .MuiDataGrid-cell.MuiDataGrid-cell--editing": {
      padding: 0,
    },
    "& .MuiDataGrid-cell--editing": {
      boxShadow: "none",
      "& .MuiInputBase-input": {
        fontSize: "13px",
      },
      "& .MuiBox-root": {
        width: "100%",
        padding: "0 4px",
      },
    },
    "& .MuiDataGrid-editInputCell": {
      padding: 0,
    },
    "& .MuiAutocomplete-root": {
      padding: 0,
    },
    "& .MuiInputBase-hiddenLabel": {
      padding: 0,
    },
    "& .MuiSelect-outlined": {
      padding: 0,
    },
    "& .MuiDataGrid-toolbarContainer": {
      padding: "0px",
      borderBottom: "1px solid #e0e0e0",
      "& .MuiButton-root": {
        padding: "12px 16px",
      },
    },
  },
};

export const DatagridAsyncV2 = <T extends DataLike>(props: Props<T>) => {
  const apiRef = useGridApiRef();
  const defaultPageSize = props.defaultLimitRows ?? rowsPerPageDefault;
  const defaultPageSizeOptions = props.rowsPerPageOptions || [
    5, 20, 50, 100, 500, 1000,
  ];

  // manage state
  // TODO: describe that use useState for what
  const { rowBooleanState, updateRowBooleanState } = useRowBooleanState();
  const { rowStringState, updateRowStringState } = useRowStringState();

  // //rowStringState editing_{filedName}
  useEffect(() => {
    props.data.forEach((row) => {
      if (!row) {
        return;
      }
      const id = row.id;
      props.colDef.map((col) => {
        if (isColString(col)) {
          const key = row[col.field];
          if (id && typeof key === "string") {
            updateRowStringState(id)("editing_" + col.field)(key);
            updateRowStringState(id)(col.field)(key);
          }
        }
        //ColString以外のeditingに要対応
      });
    });
  }, [props.data, props.colDef, updateRowStringState]);

  // validation func list
  // TODO: type arrangement
  const validationFuncList = useMemo(() => {
    return props.colDef.map((col) => {
      if (
        isColString(col) ||
        isColBoolean(col) ||
        isColEnum(col) ||
        isColDate(col) ||
        isColIntTime(col)
      ) {
        return { func: col.validate, field: col.field };
      }
    });
  }, [props.colDef]);
  const validationAsyncFuncList = useMemo(() => {
    return props.colDef.map((col) => {
      if (
        isColString(col) ||
        isColBoolean(col) ||
        isColEnum(col) ||
        isColDate(col) ||
        isColIntTime(col)
      ) {
        return { func: col.validate, field: col.field };
      }
    });
  }, [props.colDef]);

  //pagenation
  //TODO: type arrangement
  // const [variables, setVariables] = useState<RefetchProps>(props.variables ?? {...DefaultRefetchProps})
  // useEffect(() => {
  //   console.log("vari", props.variables == variables);
  //   if (props.variables && props.variables != variables) setVariables(props.variables)
  // }, [ setVariables])
  // TODO: variablesと同様にページコンポーネント側で定義する
  const [page, setPage] = useState<number>(props.dataGridPage ?? 0);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);

  //Sort
  const [sortModel, setSortModel] = useState<GridSortModel>(
    props.dataGridSortModel ?? [],
  );

  const onSortModelChange = useCallback(
    (sm: GridSortModel) => {
      setSortModel(sm);
      props.refetch({
        ...props.variables,
        order: sm?.map((o) => ({
          field: o.field,
          direction:
            o.sort === "asc"
              ? OrderDirectionEnum.Asc
              : o.sort === "desc"
                ? OrderDirectionEnum.Desc
                : undefined,
        })),
      });
    },
    [props],
  );

  //server error
  const [serverError, setServerError] = useState<string[]>([]);
  const throwServerError = useCallback((message: any) => {
    if (message) setServerError((e) => e.concat(message));
  }, []);
  useEffect(
    () => throwServerError(props.error),
    [throwServerError, props.error],
  );

  //Filter
  // TODO: variablesと同様にページコンポーネント側で定義する
  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>(
    props.dataGridFilterModel ?? {
      items: [],
      logicOperator: GridLogicOperator.And,
    },
  );

  //map (ColType => FilterType)
  const ColTypeFilterTypeMap: {
    string: "stringFilter";
    custom: "none";
    checkBox: "none";
    date: "dateFilter";
    enum: "enumFilter";
    hidden: "none";
    intTime: "intTimeFilter";
    number: "numberFilter";
    time: "stringFilter";
    boolean: "booleanFilter";
  } = useMemo(
    () => ({
      string: "stringFilter",
      custom: "none",
      checkBox: "none",
      date: "dateFilter",
      enum: "enumFilter",
      hidden: "none",
      intTime: "intTimeFilter",
      number: "numberFilter",
      time: "stringFilter",
      boolean: "booleanFilter",
    }),
    [],
  );
  //map (FieldName => ColType)
  const FieldNameColTypeMap: FieldNameColTypeMapType = Object.assign(
    {} as FieldNameColTypeMapType,
    ...props.colDef
      .filter((col): col is ColWithField<T> => "field" in col)
      .map((col) => {
        return { [col.field]: col.colType };
      }),
  );
  //Filter Model => TailorDBFilterModelItems
  const convertFilterModel = useCallback(
    (gridFilterModel: GridFilterModel) => {
      const isAnd = gridFilterModel?.logicOperator === GridLogicOperator.And;
      const queryItems = gridFilterModel?.items
        .map((item) => {
          return {
            field: item.field,
            operator: item.operator,
            value: item.value,
            filterType: ColTypeFilterTypeMap[FieldNameColTypeMap[item.field]],
          };
        })
        .map((item) => {
          if (item.filterType === "stringFilter") {
            return {
              ...item,
              operator:
                item.operator === "equals"
                  ? "eq"
                  : item.operator === "isEmpty"
                    ? "exists"
                    : item.operator === "isNotEmpty"
                      ? "exists"
                      : item.operator === "contains"
                        ? "contains"
                        : undefined,
              value:
                item.operator === "isEmpty"
                  ? false
                  : item.operator === "isNotEmpty"
                    ? true
                    : item.operator === "contains"
                      ? item.value
                      : item.value,
            };
          } else if (item.filterType === "dateFilter") {
            return {
              ...item,
              operator:
                item.operator === "equal"
                  ? "eq"
                  : item.operator === "notEqual"
                    ? "ne"
                    : item.operator === "smallerThan"
                      ? "lte"
                      : item.operator === "greaterThan"
                        ? "gte"
                        : item.operator === "isEmpty"
                          ? "exists"
                          : item.operator === "isNotEmpty"
                            ? "exists"
                            : undefined,
              value:
                item.operator === "isEmpty"
                  ? false
                  : item.operator === "isNotEmpty"
                    ? true
                    : item.value
                      ? format(item.value, "yyyy-MM-dd")
                      : undefined,
            };
          } else if (item.filterType === "enumFilter") {
            return {
              ...item,
              operator:
                item.operator === "equal"
                  ? "eq"
                  : item.operator === "notEqual"
                    ? "ne"
                    : item.operator === "isNotEmpty"
                      ? "exists"
                      : undefined,
              value: item.operator === "isNotEmpty" ? true : item.value,
            };
          } else if (
            item.filterType === "intTimeFilter" ||
            item.filterType === "numberFilter"
          ) {
            const isIntTime = item.filterType === "intTimeFilter";

            const formatValue = () => {
              if (
                item.operator === "isEmpty" ||
                item.operator === "isNotEmpty"
              ) {
                return null;
              }
              if (isIntTime && item.value) {
                // 4桁でないとクエリの結果が得られないため、末尾を0で埋める
                return String(item.value).padEnd(4, "0");
              } else {
                return item.value ? item.value : undefined;
              }
            };

            return {
              ...item,
              operator:
                item.operator === "="
                  ? "eq"
                  : item.operator === "!="
                    ? "ne"
                    : item.operator === ">"
                      ? "gt"
                      : item.operator === "<"
                        ? "lt"
                        : item.operator === ">="
                          ? "gte"
                          : item.operator === "<="
                            ? "lte"
                            : item.operator === "isEmpty"
                              ? "eq"
                              : item.operator === "isNotEmpty"
                                ? "ne"
                                : undefined,
              value: formatValue(),
            };
          } else if (item.filterType === "booleanFilter") {
            return {
              ...item,
              operator:
                item.operator === "isEmpty"
                  ? "exists"
                  : item.operator === "isNotEmpty"
                    ? "exists"
                    : item.operator === "isTrue"
                      ? "eq"
                      : item.operator === "isFalse"
                        ? "eq"
                        : undefined,
              value:
                item.operator === "isTrue"
                  ? true
                  : item.operator === "isFalse"
                    ? false
                    : item.operator === "isEmpty"
                      ? false
                      : item.operator === "isNotEmpty"
                        ? true
                        : item.value,
            };
          } else {
            return undefined;
          }
        })
        .filter(
          (item): item is NonNullable<typeof item> =>
            item != null &&
            item.operator != undefined &&
            item.field != undefined,
        );

      return convertToNestedFilterQuery(queryItems, isAnd);
    },
    [ColTypeFilterTypeMap, FieldNameColTypeMap],
  );

  //create new row
  const [creatingRowID, setCreatingRowID] = useState<string | undefined>();
  const startCreateRow = useCallback(() => {
    const newRowID = "new" + randomString(10);
    setCreatingRowID(newRowID);
    setTimeout(() => {
      apiRef.current.startRowEditMode({ id: newRowID });
      updateRowBooleanState(newRowID)("isEditing")(true);
    }, 300);
  }, [apiRef, setCreatingRowID, updateRowBooleanState]);

  //manage fetching rows
  const rowsData: T[] = useMemo(() => {
    const rowsWithID = creatingRowID
      ? ([{ id: creatingRowID as DataLike["id"] }] as T[]).concat(props.data)
      : props.data;

    return rowsWithID.map((row) => {
      const b = { ...row };
      for (const i in props.colDef) {
        const def = props.colDef[i];
        if (def && def.resolve && def.colType !== "checkbox" && def?.field) {
          const value = def.resolve(row);
          type Field = keyof typeof b;
          const field = def.field as Field;
          b[field] = value as (typeof b)[Field];
        }
      }
      return b;
    });
  }, [props, creatingRowID]);

  //TODO: getColumnWidth: () => {field: string, width: number}[]
  //TODO: setColumnWidth(width変化をsubscribeして実装): ({field: string, width: number}) => void

  const fieldList = useMemo(
    () =>
      props.colDef
        .filter((col): col is ColWithField<T> => "field" in col)
        .map((col) => {
          return col.field;
        }),
    [props.colDef],
  );

  //generate data json to import Datagrid(Mui) from Col components
  const columnsJson: GridColDef<T>[] = useMemo(
    () =>
      props.colDef
        .map((col) => {
          if (isColString(col)) {
            return ColStringDef({
              col,
              externalRowBooleanState: rowBooleanState,
              updateExternalRowBooleanState: updateRowBooleanState,
              externalRowStringState: rowStringState,
              updateExternalRowStringState: updateRowStringState,
            });
          } else if (isColNumber(col)) {
            return ColNumberDef({
              col,
              externalRowStringState: rowStringState,
              updateExternalRowBooleanState: updateRowBooleanState,
            });
          } else if (isColEnum(col)) {
            return ColEnumDef({
              col,
              externalRowBooleanState: rowBooleanState,
              updateExternalRowBooleanState: updateRowBooleanState,
              externalRowStringState: rowStringState,
            });
          } else if (isColBoolean(col)) {
            return ColBooleanDef({
              col,
              externalRowStringState: rowStringState,
              updateExternalRowBooleanState: updateRowBooleanState,
            });
          } else if (isColDate(col)) {
            return ColDateDef({
              col,
              externalRowStringState: rowStringState,
            });
          } else if (isColTime(col)) {
            return ColTimeDef({
              col,
              externalRowStringState: rowStringState,
            });
          } else if (isColIntTime(col)) {
            return ColIntTimeDef({
              col,
              externalRowStringState: rowStringState,
            });
          } else if (isColCustom(col)) {
            return ColCustomDef({
              col,
              apiRef,
              externalRowBooleanState: rowBooleanState,
              updateExternalRowBooleanState: updateRowBooleanState,
              createRow: props.createRow,
              updateRow: props.updateRow,
              deleteRow: props.deleteRow,
              refetch: () =>
                props?.variables ? props.refetch(props.variables) : {},
              setCreatingRowID,
              creatingRowID,
              throwServerError,
            });
          } else if (isColHidden(col)) {
            return ColHiddenDef({ col });
          } else {
            return undefined;
          }
        })
        .filter(
          (item): item is Exclude<typeof item, undefined> => item !== undefined,
        ),
    [
      props,
      rowBooleanState,
      updateRowBooleanState,
      rowStringState,
      updateRowStringState,
      apiRef,
      creatingRowID,
      throwServerError,
    ],
  );

  const columnVisibilityModel = columnsJson.reduce(
    (acc: Record<string, boolean>, item: Record<string, any>) => {
      if (item.hide) {
        acc[item.field] = false;
        delete item.hide;
      }
      return acc;
    },
    {},
  );

  // 型ガード関数を定義
  const hasField = useCallback(
    (col: ColWithField<T> | ColCheckBox<T>): col is ColWithField<T> => {
      return (col as ColWithField<T>).field !== undefined;
    },
    [],
  );

  //name of cols pinned to left or right side of Datagrid
  const pinnedLeftColumnsFieldName = useMemo(
    () =>
      props.colDef
        // colにfieldが存在するかのチェックを追加
        .filter((col) => hasField(col) && col.pin === "left")
        .map((col) => (hasField(col) ? col.field : "")),
    [hasField, props.colDef],
  );
  const pinnedRightColumnsFieldName = useMemo(
    () =>
      props.colDef
        .filter((col) => hasField(col) && col.pin === "right")
        .map((col) => (hasField(col) ? col.field : "")),
    [hasField, props.colDef],
  );

  //check existence of ColCheckBox
  const checkboxSelection: boolean = useMemo(
    () => !!props.colDef.find((col) => col.colType === "checkbox"),
    [props.colDef],
  );

  //get info of checked rows
  const getCheckedRows = useCallback(() => {
    // guard emptiness of apiRef while loading
    if (!apiRef.current.getSelectedRows) {
      return [];
    }
    const selectedRowsMap = apiRef.current.getSelectedRows();
    const selectedRowList: { [field: string]: any }[] = [];
    selectedRowsMap.forEach((value) => {
      selectedRowList.push(value);
    });
    return selectedRowList;
  }, [apiRef]);

  const setSubscription = useCallback(() => {
    apiRef.current.subscribeEvent(
      "rowEditStart",
      (paramsEvent: GridRowEditStartParams<any>) => {
        updateRowBooleanState(paramsEvent.id)("isEditing")(true);
      },
    );
    apiRef.current.subscribeEvent(
      "rowEditStop",
      (paramsEvent: GridRowEditStopParams<any>) => {
        apiRef.current.startRowEditMode({ id: paramsEvent.id });
      },
    );
  }, [apiRef, updateRowBooleanState]);

  //detect double click
  useEffect(() => setSubscription(), [setSubscription]);

  // header renderer
  const renderDatagridHeader = () => {
    if (props.header) {
      return (
        <Box>
          {props.header({
            getCheckedRows,
            startCreateRow,
            creatingRowID,
            throwServerError,
            setFilterModel: (model) => {
              props.refetch({
                ...props.variables,
                query: {
                  ...props.variables?.query,
                  ...model,
                },
              });
            },
          })}
        </Box>
      );
    }
  };

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      {renderDatagridHeader()}
      <Box>
        <DataGridPro
          sx={styles.datagrid}
          checkboxSelection={checkboxSelection}
          onRowSelectionModelChange={props?.onRowSelectionModelChange}
          isRowSelectable={props.isRowSelectable}
          columns={columnsJson}
          rows={rowsData}
          apiRef={apiRef}
          initialState={{
            columns: {
              columnVisibilityModel,
            },
          }}
          {...(props.dataGridColumnVisibilityModel && {
            columnVisibilityModel:
              props.dataGridColumnVisibilityModel ?? columnVisibilityModel,
          })}
          onColumnVisibilityModelChange={(model) => {
            props.onDataGridColumnVisibilityModelChange?.(model);
          }}
          pinnedColumns={
            props.dataGridPinnedColumnFields ?? {
              left: pinnedLeftColumnsFieldName,
              right: pinnedRightColumnsFieldName,
            }
          }
          onPinnedColumnsChange={(model) => {
            props.onDataGridPinnedColumnFieldsChange?.(model);
          }}
          onRowModesModelChange={(params) => {
            Object.keys(params).map((rowId) => {
              const row = fieldList.reduce((prev, field) => {
                return {
                  ...prev,
                  [field]:
                    props.data.find((row) => row?.id === rowId)?.[field] ?? "",
                };
              }, {});
              const input = Object.keys(params[rowId]).reduce((prev, field) => {
                return {
                  ...prev,
                  [field]:
                    (params[rowId] as Record<string, any>)[field].value ?? "",
                };
              }, {});

              //need validationAsync
              if (validationAsyncFuncList.length > 0) {
                updateRowBooleanState(rowId)("isLoading")(true);
                //start validation
                const validateFuncPromiseList = fieldList
                  .map((field) => {
                    return {
                      field,
                      func: validationFuncList.find(
                        (vf) => vf?.field === field,
                      ),
                      funcAsync: validationAsyncFuncList.find(
                        (vf) => vf?.field === field,
                      ),
                    };
                  })
                  .map(({ func: vf, funcAsync: vfAsync, field: field }) => {
                    if (vf && vf.func) {
                      const message = vf.func(row, input);
                      updateRowStringState(rowId)("errorMessage_" + field)(
                        message ?? "",
                      );
                      if (message !== "") {
                        return true;
                      } else {
                        if (vfAsync && vfAsync.func) {
                          const message = vfAsync.func(row, input);
                          updateRowStringState(rowId)("errorMessage_" + field)(
                            message ?? "",
                          );
                          return message !== "";
                        } else {
                          return false;
                        }
                      }
                    } else {
                      return false;
                    }
                  });
                Promise.all(validateFuncPromiseList)
                  .then((resultArray) => {
                    updateRowBooleanState(rowId)("isLoading")(false);
                    updateRowBooleanState(rowId)("isError")(
                      resultArray.some(Boolean),
                    );
                  })
                  .catch((err) => {
                    throwServerError(err);
                    updateRowBooleanState(rowId)("isLoading")(false);
                  });
              }
            });
          }}
          loading={props.loading}
          onFilterModelChange={(model) => {
            const filterQuery = convertFilterModel(model);
            const mergedQuery = mergeQueryVariables(
              { ...props.variables },
              filterQuery,
            );
            props.onDataGridFilterModelChange?.(model);
            setGridFilterModel(model);
            props.refetch(mergedQuery);
          }}
          filterModel={gridFilterModel}
          filterMode={props.filterMode || "server"}
          editMode="row"
          sortModel={sortModel}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({
            page: newPage,
            pageSize: newPageSize,
          }) => {
            setPage(newPage);
            props.onDataGridPageChange?.(newPage);
            // PDFエクスポートとCSVエクスポートの不具合対応
            // https://tailorpartners.slack.com/archives/C072BD02GDS/p1743078913262179
            // フィルターされた状況で、フィルターされた状況でのトータル件数より、表示データ件数が多い場合、エクスポートを行う場合に、onPagenationModalChangeがnewPageSizeを表示されているデータ数(ex.31)として呼ばれている。Datagridは、newPageSizeとして、defaultPageSizeOptionsしか受け付けないため、不具合となっている。そのため、newPageSizeがdefaultPageSizeOptionsに含まれる場合は、そのまま使用し、それ以外の場合は、pageSizeをそのまま使うように修正する
            const newPageSizeFormatted = defaultPageSizeOptions.includes(
              newPageSize,
            )
              ? newPageSize
              : pageSize;
            setPageSize(newPageSizeFormatted);
            // PDFエクスポートとCSVエクスポートの不具合対応
            // https://tailorpartners.slack.com/archives/C072BD02GDS/p1744007245635229?thread_ts=1743078913.262179&cid=C072BD02GDS
            // フィルターされた状況で、表示データ件数と合計件数が同じ場合、refetchが行われないため、returnする
            if (newPageSizeFormatted === props.total) {
              return;
            }
            const after = btoa(`offset:${newPage * newPageSizeFormatted}`);
            props.refetch({
              ...props.variables,
              first: newPageSizeFormatted ?? defaultPageSize,
              after: after,
            });
          }}
          pageSizeOptions={defaultPageSizeOptions}
          pagination
          paginationMode={props.paginationMode || "server"}
          rowCount={props.total}
          onSortModelChange={onSortModelChange}
          rowHeight={30}
          columnHeaderHeight={42}
          localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
          slots={{
            loadingOverlay: GridLoadingOverlay,
            toolbar: () => (
              <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarDensitySelector />
                <GridToolbarExport
                  csvOptions={{
                    delimiter: ",",
                    utf8WithBom: true,
                  }}
                />
              </GridToolbarContainer>
            ),
          }}
        />
      </Box>
      <ErrorLayer errors={serverError} />
    </Stack>
  );
};
